import { useEffect, useRef } from 'react';

type NavigatorWakeLock = Navigator & {
  wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinel> };
};

function getWakeLock() {
  return (navigator as NavigatorWakeLock).wakeLock;
}

/** Screen Wake Lock while `enabled` (HTTPS). Gesture + visibility + bfcache for broad mobile support. */
export function useScreenWakeLock(enabled: boolean) {
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const sentinelRef = useRef<WakeLockSentinel | null>(null);

  const hasActiveLock = () => {
    const s = sentinelRef.current;
    return Boolean(s && !s.released);
  };

  function assignLock(lock: WakeLockSentinel) {
    const prev = sentinelRef.current;
    if (prev && prev !== lock && !prev.released) {
      void prev.release().catch(() => {});
    }
    sentinelRef.current = lock;
    lock.addEventListener('release', () => {
      if (sentinelRef.current === lock) sentinelRef.current = null;
    });
  }

  function releaseLockSync() {
    const s = sentinelRef.current;
    if (!s) return;
    sentinelRef.current = null;
    void s.release().catch(() => {});
  }

  function canAcquire(): boolean {
    return (
      enabledRef.current &&
      typeof document !== 'undefined' &&
      document.visibilityState === 'visible' &&
      !!getWakeLock()?.request &&
      !hasActiveLock()
    );
  }

  /** Call stack must originate from a user gesture (tap, key) for strict browsers (e.g. iOS Safari). */
  function tryAcquireFromUserGesture() {
    if (!canAcquire()) return;
    const w = getWakeLock();

    void w
      .request('screen')
      .then((lock) => {
        if (!enabledRef.current) {
          void lock.release().catch(() => {});
          return;
        }
        assignLock(lock);
      })
      .catch(() => {});
  }

  /** Works on many Android/desktop browsers without a gesture; may fail on iOS until user interacts. */
  async function tryAcquireInBackground() {
    if (!canAcquire()) return;
    const w = getWakeLock();

    try {
      const lock = await w.request('screen');
      if (!enabledRef.current) {
        await lock.release().catch(() => {});
        return;
      }
      assignLock(lock);
    } catch {
      // expected when gesture is required
    }
  }

  useEffect(() => {
    if (!enabled) return;

    const onGesture = () => tryAcquireFromUserGesture();
    const opts: AddEventListenerOptions = { capture: true, passive: true };

    document.addEventListener('pointerdown', onGesture, opts);
    document.addEventListener('touchstart', onGesture, opts);
    document.addEventListener('keydown', onGesture, opts);

    return () => {
      document.removeEventListener('pointerdown', onGesture, opts);
      document.removeEventListener('touchstart', onGesture, opts);
      document.removeEventListener('keydown', onGesture, opts);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      releaseLockSync();
      return;
    }

    void tryAcquireInBackground();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void tryAcquireInBackground();
    };

    const onPageShow = () => void tryAcquireInBackground();
    const onWindowFocus = () => void tryAcquireInBackground();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onWindowFocus);
      releaseLockSync();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}
